﻿import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';

import { useAlert } from '../../hooks/useAlert';
import { useUserRole } from '../../hooks/useUserRole';
import { getTaskStatusApi, mergeTaskApi } from '../../service/task';
import { getSeriesListApi } from '../../service/series';
import {
    backTranslateApi,
    proofreadApi,
    adjustTaskVolumeApi,
    generateVideoApi,
    batchUpdatePostTextApi,
    retranslateSubtitleApi,
    audioRegenerateApi,
} from '../../service/subtitles';
import {
    buildBatchUpdatePostTextRequestPayload,
    formatSubtitlePostTextForListDisplay,
} from '../../utils/subtitlePostText';
import { getSubtitlesListApi } from '../../service/task';
import { getLanguageName } from '../../utils/languageUtils';
import {
    buildPremiumVideoListRefactorReturnAfterVideoEditUrl,
    INNER_SERIES_LIST_PAGE_PARAM,
    OUTER_SERIES_LIST_PAGE_PARAM,
    PREMIUM_TRANSLATION_FULL_PATH,
} from '../../utils/menuPathNormalize';
import { audioManager } from '../../utils/audioManager';
import { globalStore } from '../../utils/globalStore';

let GLOBAL_PLAYING_AUDIO_ID: string | null = null;

/** 与 initService 写入的 `userInfo` 一致；另兼容 globalStore 持久化的 `globalUserInfo` */
function loadUserInfoFromStorages(): any {
    try {
        const raw = localStorage.getItem('userInfo');
        if (raw) return JSON.parse(raw);
    } catch {
        // ignore
    }
    try {
        const raw = localStorage.getItem('globalUserInfo');
        if (raw) return JSON.parse(raw);
    } catch {
        // ignore
    }
    try {
        return globalStore.getUserInfo();
    } catch {
        return null;
    }
}

/** 预览水印文案：兼容后端/历史字段，避免仅有兜底「用户」 */
function resolveWatermarkDisplayName(u: unknown): string {
    if (!u || typeof u !== 'object') return '用户';
    const o = u as Record<string, unknown>;
    const pick = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
    const label =
        pick(o.nickname) ||
        pick(o.nickName) ||
        pick(o.username) ||
        pick(o.userName) ||
        pick(o.name) ||
        pick(o.realName);
    if (label) return label;
    const email = pick(o.email);
    if (email) {
        const at = email.indexOf('@');
        return at > 0 ? email.slice(0, at) : email;
    }
    const mobile = pick(o.mobile) || pick(o.phone);
    if (mobile) return mobile;
    const id = o.id;
    if (typeof id === 'number' && id > 0) return `ID${id}`;
    if (typeof id === 'string' && /^\d+$/.test(id)) return `ID${id}`;
    return '用户';
}

import { useTimeboxController } from './videoTimeBox';
import { useTextListController } from './videoTextList';
import { useVideoShowController } from './videoShow';

// Window interface extension for prefetched audios
declare global {
    interface Window {
        prefetchedTransAudios?: Map<string, HTMLAudioElement>;
    }
}

type Subtitle = Series.Subtitle;

export const useVideoEditPage = () => {
    const [searchParams] = useSearchParams();
    const seriesId = searchParams.get('seriesId');
    const urlLangCode = searchParams.get('langCode');
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { alertConfig, hideAlert, showSuccess, showError, showWarning } = useAlert();
    const { isAdmin, roleInfo } = useUserRole();

    // 获取当前用户信息（首帧同步读缓存，避免水印先闪「用户」再等 effect）
    const [userInfo, setUserInfo] = useState<any>(() => loadUserInfoFromStorages());

    useEffect(() => {
        const user = loadUserInfoFromStorages();
        if (user) {
            setUserInfo(user);
        }
    }, []);

    const displayName = useMemo(() => resolveWatermarkDisplayName(userInfo), [userInfo]);

    // 从location.state获取视频名称，如果没有则使用默认值
    const videoName = useMemo(() => {
        return (location.state as any)?.videoName || `视频${id || '10'}`;
    }, [location.state, id]);

    /** 原剧/纯净列表：state 或编辑页 URL 上 isPure=true */
    const isFromPure = useMemo(() => {
        const fromState = (location.state as { isPure?: boolean } | null)?.isPure === true;
        return fromState || searchParams.get('isPure') === 'true';
    }, [location.state, searchParams]);

    /** 从视频列表进入编辑时在 state 中携带的列表页码，用于返回/校对/合成后回到同一页 */
    const videoListReturnPage = useMemo(() => {
        const raw = (location.state as { listReturnPage?: unknown } | null)?.listReturnPage;
        const n = typeof raw === 'number' ? raw : typeof raw === 'string' ? parseInt(raw, 10) : NaN;
        return Number.isFinite(n) && n > 1 ? Math.floor(n) : undefined;
    }, [location.state]);

    /** 内层语种列表页码：经视频列表 query `innerListPage` 传到编辑页，返回视频列表时写回 */
    const innerSeriesListReturnPage = useMemo(() => {
        const raw = searchParams.get(INNER_SERIES_LIST_PAGE_PARAM);
        const n = parseInt(raw || '', 10);
        return Number.isFinite(n) && n > 1 ? Math.floor(n) : undefined;
    }, [searchParams]);

    /** 外层剧集列表页码：经列表/编辑 query `outerListPage` 传递，返回视频列表时写回 */
    const outerSeriesListReturnPage = useMemo(() => {
        const raw = searchParams.get(OUTER_SERIES_LIST_PAGE_PARAM);
        const n = parseInt(raw || '', 10);
        return Number.isFinite(n) && n > 1 ? Math.floor(n) : undefined;
    }, [searchParams]);

    // 视频数据状态
    const [videoData, setVideoData] = useState<Task.GetTaskStatusResult | null>(null);

    // 【终极修复】创建一个“特洛伊木马”式的 videoData 对象
    // 策略：

    const videoDataForDemo = useMemo(() => {
        if (!videoData) return null;
        // 浅拷贝；依赖须包含 subtitles：否则仅字幕变更时引用不变，VideoPreviewRight(memo) 与子组件收不到新 props，画布译文不更新
        return { ...videoData };
    }, [
        videoData?.id,
        videoData?.video_hls_url,
        (videoData as any)?.pure_video_hls_url,
        videoData?.subtitles,
        videoData?.subtitleLineNumber,
    ]);

    // 【修复补充】为 localSubtitles 也创建“特洛伊木马”对象 (Stable Array)
    // 防止 subtitles 数组引用变化导致 SubtitleOverlayPlayer 重载视频
    const localSubtitlesForDemo = useMemo(() => {
        return [] as Task.SubtitlesLegacy[];
    }, [id]); // 仅在 ID 变化时（切换视频）才重建

    const [isGenerating, setIsGenerating] = useState(false);
    
    // 字幕更新状态
    const [updatingSubtitles, setUpdatingSubtitles] = useState<{ [key: number]: boolean }>({});
    const [subtitleUpdateErrors, setSubtitleUpdateErrors] = useState<{ [key: number]: string }>({});
    
    // 全局操作状态
    const [isGlobalRetranslating, setIsGlobalRetranslating] = useState(false);
    const [isGlobalRedubbing, setIsGlobalRedubbing] = useState(false);
    // 跟踪每个台词的翻译/配音状态
    const [subtitleProcessingStates, setSubtitleProcessingStates] = useState<Record<number, { isRetranslating: boolean; isRedubbing: boolean }>>({});
    const [showGlobalRetranslateDropdown, setShowGlobalRetranslateDropdown] = useState(false);
    const [globalCustomText, setGlobalCustomText] = useState('');
    const [isArrowHovered, setIsArrowHovered] = useState(false); // 箭头悬停状态
    const globalRetranslateCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [isAdjustingSpeed, setIsAdjustingSpeed] = useState(false); // 倍速调节加载状态
    const [showBackTranslate, setShowBackTranslate] = useState(false); // 显示/隐藏回译内容
    const [backTranslateData, setBackTranslateData] = useState<Task.Subtitles[]>([]); // 回译数据
    const [isBackTranslating, setIsBackTranslating] = useState(false); // 回译加载状态
    // 背景音乐音量调节弹窗状态（backgroundVolume 初始值由接口数据决定）
    const [showBackgroundVolumePopup, setShowBackgroundVolumePopup] = useState(false);
    const [backgroundVolumePopupPosition, setBackgroundVolumePopupPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
    const [backgroundVolume, setBackgroundVolume] = useState<number>(1);
    // 待同步的时间轴调节数据
    const [pendingTimelineUpdates, setPendingTimelineUpdates] = useState<Map<number, {
        audio_speed: number;
        post_start_time: string;
        post_end_time: string;
    }>>(new Map());
    
    const clearGlobalRetranslateCloseTimer = useCallback(() => {
        if (globalRetranslateCloseTimerRef.current) {
            clearTimeout(globalRetranslateCloseTimerRef.current);
            globalRetranslateCloseTimerRef.current = null;
        }
    }, []);

    const openGlobalRetranslateDropdown = useCallback(() => {
        clearGlobalRetranslateCloseTimer();
        setIsArrowHovered(true);
        setShowGlobalRetranslateDropdown(true);
    }, [clearGlobalRetranslateCloseTimer]);

    const scheduleCloseGlobalRetranslateDropdown = useCallback(() => {
        clearGlobalRetranslateCloseTimer();
        globalRetranslateCloseTimerRef.current = setTimeout(() => {
            setIsArrowHovered(false);
            setShowGlobalRetranslateDropdown(false);
        }, 180);
    }, [clearGlobalRetranslateCloseTimer]);

    const setVideoDataSafe = useCallback((newVideoData: Task.GetTaskStatusResult | null) => {
        // 设置videoData
        if (newVideoData && id) {
            const currentId = parseInt(id);
            if (newVideoData.id !== currentId) {
                // 检测到videoData ID不一致，已自动修正
                newVideoData.id = currentId;
            }
        }
        setVideoData(newVideoData);
    }, [id]);

    const [currentEpisodeInfo, setCurrentEpisodeInfo] = useState({
        name: '',
        sourceLanguage: '',
        targetLanguage: '',
        subtitleErase: false
    });

    // Shared targetText state (timebox + textList both need it)
    const [targetText, setTargetText] = React.useState<Array<{ id: number; text: string; timeRange: string; [key: string]: any }>>([]);
    // Shared loading state so timebox effect fires after VideoTimeBox is in DOM
    const [isLoadingVideoPage, setIsLoadingVideoPage] = React.useState(false);
    // Stable ref bridges to break circular dep between timebox ↔ videoShow
    const onSwitchTrackRef = useRef<(track: 'translated' | 'original') => void>(() => {});
    const onPlayheadMoveRef = useRef<(time: number) => void>(() => {});
    /** timebox 早于 textList 创建：用 ref 将原文时间轴校准同步到左侧原词列表 */
    const setSourcesTextFromTimelineRef = useRef<
        React.Dispatch<React.SetStateAction<Array<{ id: number; text: string; timeRange: string }>>> | null
    >(null);
    /** 与 React 同步：列表高亮 ?? 时间轴选中，供 playing 与点击后首帧 highlight */
    const segmentPlayingLockLineIdRef = useRef<number | null>(null);

    const onOriginTimestampSaved = useCallback((subtitleId: number, originStart: string, originEnd: string) => {
        setSourcesTextFromTimelineRef.current?.((prev) =>
            prev.map((item) =>
                item.id === subtitleId ? { ...item, timeRange: `${originStart} → ${originEnd}` } : item
            )
        );
    }, []);

    // ============ Call sub-hooks ============

    const timebox = useTimeboxController({
        videoData,
        isLoadingVideo: isLoadingVideoPage,
        duration: 0,
        targetText,
        setVideoData,
        setTargetText,
        onPlayheadMove: useCallback((t) => onPlayheadMoveRef.current(t), []),
        onSwitchTrack: useCallback((track) => onSwitchTrackRef.current(track), []),
        isFromPure,
        onOriginTimestampSaved,
        segmentPlayingLockLineIdRef,
    });

    const textList = useTextListController({
        videoData,
        setVideoData,
        setVideoDataSafe,
        id,
        seriesId,
        urlLangCode,
        isFromPure,
        showError,
        showSuccess,
        showWarning,
        timelineEditorRef: timebox.timelineEditorRef,
        pendingTimelineUpdates: timebox.pendingTimelineUpdates,
        targetText,
        setTargetText,
    });
    setSourcesTextFromTimelineRef.current = textList.setSourcesText;
    segmentPlayingLockLineIdRef.current =
        textList.highlightedSubtitleId ?? timebox.timelineSelectedSubtitleId ?? null;

    const videoShow = useVideoShowController({
        videoData,
        setVideoData,
        setVideoDataSafe,
        id,
        isFromPure,
        showError,
        showSuccess,
        showWarning,
        timelineEditorRef: timebox.timelineEditorRef,
        timelineScrubbingRef: timebox.timelineScrubbingRef,
        translatedTrackSegmentsRef: timebox.translatedTrackSegmentsRef,
        translatedTrackBuildSignature: timebox.translatedTrackBuildSignature,
        setPlaybackUiTime: timebox.setPlaybackUiTime,
        currentTimeRef: timebox.currentTimeRef,
        playheadTotalTimeRef: timebox.playheadTotalTimeRef,
        targetText,
        sourcesText: textList.sourcesText,
        highlightedSubtitleId: textList.highlightedSubtitleId,
        updateTargetSubtitleHighlight: textList.updateTargetSubtitleHighlight,
        setHighlightedSubtitleId: textList.setHighlightedSubtitleId,
        processedSubtitlesRef: timebox.processedSubtitlesRef,
        scrollToHighlightedSubtitle: textList.scrollToHighlightedSubtitle,
        volumeMap: textList.volumeMap,
        pendingTimelineUpdates: timebox.pendingTimelineUpdates,
    });

    // Update bridges each render so they always point to current handlers
    onSwitchTrackRef.current = videoShow.handleSwitchTrack;
    onPlayheadMoveRef.current = videoShow.handlePlayheadMove;

    const handleSubtitlePostTextChange = useCallback(
        (payload: { id: number; postText: string }) => {
            setVideoData((prev) => {
                if (!prev?.subtitles) return prev;
                return {
                    ...prev,
                    subtitles: prev.subtitles.map((s) =>
                        s.id === payload.id ? { ...s, post_text: payload.postText } : s
                    ),
                };
            });
            setTargetText((prev) =>
                prev.map((item) =>
                    item.id === payload.id ? { ...item, text: payload.postText } : item
                )
            );
            const timeline = timebox.timelineEditorRef.current;
            if (timeline) {
                const latest = videoData?.subtitles?.find((s) => s.id === payload.id);
                if (latest) {
                    timeline.updateSegment(payload.id, {
                        ...latest,
                        post_text: payload.postText,
                    });
                }
            }
        },
        [setVideoData, timebox.timelineEditorRef, videoData?.subtitles]
    );

    /** 点击右侧目标台词：在控制台输出带 \\N 的批量更新请求体（与合成前 batch 一致） */
    const handleTargetDialogueClick = useCallback(() => {
        const taskId = videoData?.id;
        if (!taskId) {
            console.warn('[批量更新目标台词] taskId 缺失');
            return;
        }
        const subtitles = videoShow.subtitleOverlayPlayerRef.current?.buildBatchPostTexts();
        if (!subtitles?.length) {
            console.warn(
                '[批量更新目标台词] 无法生成请求体（右侧字幕框未就绪或未加载字幕）'
            );
            return;
        }
        const payload = buildBatchUpdatePostTextRequestPayload(taskId, subtitles);
        console.log(
            '[批量更新目标台词] PUT /video/subtitles/batch-update-post-text 请求参数:',
            payload
        );
        console.log(JSON.stringify(payload, null, 2));
    }, [videoData?.id, videoShow.subtitleOverlayPlayerRef]);

    /** 本集各说话人台词条数：按 speakers.id 统计，与 RowItem / RoleDropdown 用 currentSpeakerId 查表一致（不依赖名称与 roles 列表） */
    const speakerLineCounts = useMemo(() => {
        const counts: Record<number, number> = {};
        for (const sub of videoData?.subtitles ?? []) {
            const sid = sub.speakers?.id;
            if (typeof sid === 'number' && sid > 0) {
                counts[sid] = (counts[sid] ?? 0) + 1;
            }
        }
        return counts;
    }, [videoData?.subtitles]);

    useEffect(() => {
        const geteriesData = async () => {
            if (seriesId) {
                  // 获取剧集信息（后端分页上限为 500，这里使用 500 避免超出限制）
                  const response = await getSeriesListApi({
                    pageNo: 1,
                    pageSize: 500
                });
                // 查找匹配的剧集
                const targetSeries = (response.data?.list as any[])?.find((series: any) => series.id === parseInt(seriesId!));

                if (targetSeries) {
                    // 更新抽屉信息
                    setCurrentEpisodeInfo({
                        name: targetSeries.name,
                        sourceLanguage: targetSeries.source_lang || '',
                        targetLanguage: targetSeries.target_lang || '',
                        subtitleErase: targetSeries.need_removal_subtitle || false
                    });
                }
            }
        }


        const fetchVideoData = async () => {
            if (!id) {
                console.warn('视频ID不存在');
                return;
            }

            try {
                videoShow.setIsLoadingVideo(true);
                setIsLoadingVideoPage(true);
                // 调用新的任务状态接口
                const taskData = await getTaskStatusApi(parseInt(id));
                
                // 确保返回的数据ID与当前ID一致
                if (taskData.id !== parseInt(id)) {
                    console.warn(`API返回的ID(${taskData.id})与当前ID(${id})不一致，强制设置为当前ID`);
                    taskData.id = parseInt(id);
                }
                
                // 使用任务级 volumeGainDb 初始化背景音量（如果后端有返回）
                const taskVolume = (taskData as any).volumeGainDb;
                if (typeof taskVolume === 'number') {
                    // 仅写入状态；不在 fetch 链上初始化 Web Audio（无用户手势会触发浏览器策略警告）
                    setBackgroundVolume(taskVolume);
                }

                // 获取字幕列表（独立接口）
                let subtitlesLegacy: Task.SubtitlesLegacy[] = [];
                try {
                    const subtitlesResponse = await getSubtitlesListApi(parseInt(id));
                    if (subtitlesResponse.code === 0 && subtitlesResponse.data) {
                        // 将新接口格式（驼峰命名）转换为旧格式（下划线命名，保持兼容性）
                        subtitlesLegacy = subtitlesResponse.data.map((subtitle: Task.Subtitles) => ({
                            task_id: subtitle.taskId,
                            origin_text: subtitle.originText,
                            post_text: subtitle.postText || '',
                            post_start_time: subtitle.postStartTime || '',
                            post_end_time: subtitle.postEndTime || '',
                            origin_start_time: subtitle.originStartTime,
                            origin_end_time: subtitle.originEndTime,
                            origin_post_time: subtitle.originPostTime,
                            is_episode_voice: subtitle.isEpisodeVoice || false,
                            origin_audio: subtitle.originAudio,
                            trans_audio: subtitle.transAudio || '',
                            subtitle_id: subtitle.subtitleId || subtitle.id,
                            score: subtitle.score,
                            audio_speed: subtitle.audioSpeed || 1.0,
                            volume_gain_db: subtitle.volumeGainDb || 1.0,
                            id: subtitle.id,
                            speakers: {
                                id: subtitle.speaker.id,
                                series_id: subtitle.speaker.seriesId,
                                task_id: subtitle.taskId,
                                speaker_name: subtitle.speaker.speakerName,
                                description: subtitle.speaker.description || '',
                                is_series_speaker: subtitle.speaker.isSeriesSpeaker
                            },
                            voice_info: {
                                origin_text: subtitle.originText,
                                origin_start_time: subtitle.originStartTime,
                                origin_end_time: subtitle.originEndTime,
                                origin_audio: subtitle.originAudio,
                                score: subtitle.score
                            },
                            back_translate_text: subtitle.backTranslateText || '',
                            words: subtitle.words?.map(word => ({
                                id: word.id,
                                subtitle_id: word.subtitleId,
                                position: word.position,
                                origin_char: word.originChar,
                                start_ms: word.startMs,
                                end_ms: word.endMs
                            })) || [],
                            notes: subtitle.remark || undefined, // 兼容旧字段名（新接口使用 remark）
                            voiceSourceType: subtitle.voiceSourceType,
                            publicVoiceToneId: subtitle.publicVoiceToneId,
                            userVoiceToneId: subtitle.userVoiceToneId
                        }));
                        // 成功获取字幕列表
                    }
                } catch (subtitleError) {
                    console.warn('【fetchVideoData】获取字幕列表失败:', subtitleError);
                }
                
                // 将字幕数据添加到 taskData 中（兼容旧代码）
                (taskData as any).subtitles = subtitlesLegacy;
                
                setVideoDataSafe(taskData);
                
                // 获取说话人列表
                await textList.fetchSpeakersList(parseInt(id), taskData);
                
                
                // 初始化每行的角色选择，使用字幕数据中的说话人
                if (subtitlesLegacy.length > 0) {
                    const initialRowRoles: { [key: number]: string } = {};
                    subtitlesLegacy.forEach(subtitle => {
                        if (subtitle.speakers && subtitle.speakers.speaker_name) {
                            initialRowRoles[subtitle.id] = subtitle.speakers.speaker_name;
                        }
                    });
                    textList.setRowSelectedRoles(initialRowRoles);
                    // console.log('初始化角色选择:', initialRowRoles);
                }
            } catch (error: any) {
                // 构建详细的错误信息
                let errorMessage = '获取视频数据失败';
                let consoleMessage = '获取视频数据失败:';

                if (error?.status) {
                    errorMessage += ` (状态码: ${error.status})`;
                    consoleMessage += `\n状态码: ${error.status}`;
                }
                if (error?.statusText) {
                    consoleMessage += `\n状态文本: ${error.statusText}`;
                }
                if (error?.response?.message) {
                    errorMessage += ` - ${error.response.message}`;
                    consoleMessage += `\n服务器消息: ${error.response.message}`;
                }
                if (error?.response) {
                    consoleMessage += `\n响应数据: ${JSON.stringify(error.response)}`;
                }
                if (error?.message) {
                    errorMessage += ` - ${error.message}`;
                    consoleMessage += `\n错误消息: ${error.message}`;
                }
                consoleMessage += `\n请求URL: /translate/api/task/status/${id}`;

                // 在控制台输出详细错误信息
                console.error(consoleMessage);
                console.error('完整错误对象:', error);

                // 在页面上显示错误信息
                showError(errorMessage);

                // 如果API调用失败，使用默认数据
                const currentId = parseInt(id);
                const fallbackData: Task.GetTaskStatusResult = {
                    name: videoName,
                    disable_bgm: false,
                    has_user_srt: false,
                    has_user_script: false,
                    tag: 0,
                    descript: null,
                    id: currentId, // 确保使用当前正确的ID
                    series_id: 0,
                    user_id: 1,
                    status: 2,
                    create_time: new Date().toISOString(),
                    update_time: new Date().toISOString(),
                    file_url: "",
                    result_url: "",
                    translated_subtitle_url: "",
                    subtitle_url: "",
                    pure_video_url: "",
                    pure_video_hls_url: "",
                    video_hls_url: "",
                    cover_url: "",
                    backgroud_audio_url: "",
                    translated_vocals_hls_url: "",
                    backgroud_audio_hls_url: "",
                    vocals_url: "",
                    vocals_hls_url: "",
                    script_url: null,
                    subtitles: [],
                    // TaskStatus 必需属性
                    coverUrl: null,
                    fileUrl: "",
                    subtitleUrl: null,
                    videoHlsUrl: null,
                    pureVideoHlsUrl: null,
                    vocalsHlsUrl: null,
                    backgroudAudioHlsUrl: null,
                    hasUserSrt: false,
                    scriptUrl: null,
                    hasUserScript: false,
                    episodeIndex: 0,
                    langCode: "",
                    pureVideoUrl: "",
                    resultUrl: null,
                    duration: null,
                    createTime: new Date().toISOString(),
                    updateTime: new Date().toISOString(),
                    disableBgm: false
                };
                console.log(`使用回退数据，确保ID为: ${currentId}`, fallbackData);
                setVideoDataSafe(fallbackData);
            } finally {
                videoShow.setIsLoadingVideo(false);
                setIsLoadingVideoPage(false);
            }
        };

        geteriesData();
        fetchVideoData();
    }, [id, seriesId]); // 移除videoName依赖，避免location.state变化导致重复调用

    // 如果没有seriesId，尝试从视频数据的series_id获取语言信息
    useEffect(() => {
        const fetchSeriesLanguageInfo = async () => {
            // 如果有seriesId，语言信息已经在geteriesData中获取，不需要再次获取
            if (seriesId) {
                return;
            }
            
            // 如果没有videoData或series_id，无法获取语言信息
            if (!videoData || !videoData.series_id) {
                return;
            }
            
            try {
                // 从API获取剧集信息以获取语言信息（分页上限 500）
                const response = await getSeriesListApi({
                    pageNo: 1,
                    pageSize: 500
                });
                
                // 查找匹配的剧集
                const targetSeries = (response.data?.list as any[])?.find((series: any) => series.id === videoData.series_id);
                
                if (targetSeries) {
                    // 更新语言信息和剧集名称
                    setCurrentEpisodeInfo(prev => ({
                        ...prev,
                        name: targetSeries.name || prev.name,
                        sourceLanguage: targetSeries.source_lang || prev.sourceLanguage || 'xx',
                        targetLanguage: targetSeries.target_lang || prev.targetLanguage || 'xx',
                        subtitleErase: targetSeries.need_removal_subtitle !== undefined 
                            ? targetSeries.need_removal_subtitle 
                            : prev.subtitleErase
                    }));
                } else {
                    // 如果找不到剧集，使用默认值
                    setCurrentEpisodeInfo(prev => ({
                        ...prev,
                        sourceLanguage: prev.sourceLanguage || 'xx',
                        targetLanguage: prev.targetLanguage || 'xx'
                    }));
                }
            } catch (error) {
                console.error('获取剧集语言信息失败:', error);
                // 如果API调用失败，使用默认值
                setCurrentEpisodeInfo(prev => ({
                    ...prev,
                    sourceLanguage: prev.sourceLanguage || 'xx',
                    targetLanguage: prev.targetLanguage || 'xx'
                }));
            }
        };
        
        fetchSeriesLanguageInfo();
    }, [videoData?.series_id, seriesId]); // 依赖videoData.series_id和seriesId

    const handleBack = () => {
        // 立即清除页面内播放的所有音频，不要播放任何音频
        
        // ================== 【必须加】优先检查全局变量：点击返回按钮时，也检查全局变量 ==================
        if (GLOBAL_PLAYING_AUDIO_ID) {
            try {
                if (typeof audioManager !== 'undefined') {
                    console.log('返回按钮：暴力停止全局音频:', GLOBAL_PLAYING_AUDIO_ID);
                    audioManager.stopSound(GLOBAL_PLAYING_AUDIO_ID);
                    audioManager.destroySound(GLOBAL_PLAYING_AUDIO_ID);
                }
            } catch (e) {
                console.error('返回按钮：停止全局音频失败', e);
            }
            GLOBAL_PLAYING_AUDIO_ID = null; // 停止后重置
        }
        // ================== 【必须加】结束 ==================
        
        // ================== 【修复】开始 ==================
        // 优先停止 AudioManager 管理的所有音频（Howler.js）
        // 使用 stopAll() 和 destroyAllSounds() 确保所有音频都被停止
        try {
            if (typeof audioManager !== 'undefined') {
                // 先使用全局停止方法停止所有音频
                if (audioManager.stopAll) {
                    audioManager.stopAll();
                    console.log('返回按钮：已停止所有 AudioManager 音频');
                }
                // 然后销毁所有音频实例
                if (audioManager.destroyAllSounds) {
                    audioManager.destroyAllSounds();
                    console.log('返回按钮：已销毁所有 AudioManager 音频实例');
                }
            }
        } catch (error) {
            console.error('返回按钮：停止/销毁所有 AudioManager 音频失败', error);
        }
        
        // 同时尝试停止特定的音频实例（三重保障）
        const instance = videoShow.currentPlayingInstanceRef.current;
        if (instance && instance.type === 'target' && instance.audioId) {
            try {
                audioManager.stopSound(instance.audioId);
                audioManager.destroySound(instance.audioId);
                console.log('返回按钮：已停止特定 AudioManager 音频', instance.audioId);
            } catch (error) {
                // 忽略错误，因为 stopAll 和 destroyAllSounds 已经处理了
            }
        }
        
        // 最激进的停止方法：直接操作 DOM 中的所有 audio 元素
        try {
            const allAudioElements = document.querySelectorAll('audio');
            allAudioElements.forEach((audio: HTMLAudioElement) => {
                try {
                    audio.pause();
                    audio.currentTime = 0;
                    audio.src = '';
                } catch (error) {
                    // 忽略错误
                }
            });
            console.log('返回按钮：已停止所有 DOM audio 元素');
        } catch (error) {
            console.error('返回按钮：停止 DOM audio 元素失败', error);
        }
        // ================== 【修复】结束 ==================
        
        // 停止翻译音频
        videoShow.stopTransAudio();
        // 停止原始音频
        videoShow.stopOriginAudio();
        // 停止当前播放实例
        videoShow.stopCurrentPlayingInstance();
        // 停止背景音频
        if (videoShow.backgroundAudioRef.current && !videoShow.backgroundAudioRef.current.paused) {
            videoShow.backgroundAudioRef.current.pause();
            videoShow.backgroundAudioRef.current.currentTime = 0;
        }
        // 停止人声音频
        if (videoShow.vocalsAudioRef.current && !videoShow.vocalsAudioRef.current.paused) {
            videoShow.vocalsAudioRef.current.pause();
            videoShow.vocalsAudioRef.current.currentTime = 0;
        }
        // 停止源视频
        if (videoShow.sourceVideoRef.current && !videoShow.sourceVideoRef.current.paused) {
            videoShow.sourceVideoRef.current.pause();
        }
        // 停止字幕演示
        if (videoShow.subtitleOverlayPlayerRef.current) {
            try { 
                videoShow.subtitleOverlayPlayerRef.current.pause(); 
            } catch (e) {}
        }
        // 清除播放状态
        videoShow.setIsPlaying(false);
        videoShow.setSubtitlePlayEndTime(null);
        
        // ================== 【新增】开始 ==================
        // 使用 setTimeout 确保音频停止操作完成后再导航
        // 这可以确保音频真正停止，而不是在导航过程中继续播放
        setTimeout(() => {
            // 如果有 seriesId 参数，带上它和剧集名称导航到对应的视频列表页
            const finalSeriesId = seriesId || videoData?.series_id;
            if (finalSeriesId) {
                const seriesNameResolved =
                    currentEpisodeInfo.name?.trim() ||
                    searchParams.get('seriesName')?.trim() ||
                    undefined;
                navigate(
                    buildPremiumVideoListRefactorReturnAfterVideoEditUrl({
                        seriesId: finalSeriesId,
                        seriesName: seriesNameResolved,
                        langCode: urlLangCode,
                        langName: searchParams.get('langName'),
                        isPure: isFromPure,
                        fromQuick: searchParams.get("from") === "quick",
                        listReturnPage: videoListReturnPage,
                        innerListPage: innerSeriesListReturnPage,
                        outerListPage: outerSeriesListReturnPage,
                    })
                );
            } else {
                navigate(PREMIUM_TRANSLATION_FULL_PATH);
            }
        }, 50); // 50ms 延迟，确保音频停止操作完成
        // ================== 【新增】结束 ==================
    };

    const handleProofread = async () => {
        if (!videoData?.id) {
            showError('任务ID不存在，无法提交校对状态');
            return;
        }
        try {
            setIsGenerating(true);
            const res = await proofreadApi(videoData.id);
            if (res.code === 0) {
                showSuccess('已校对');
                // 接口成功后返回视频列表页
                handleBack();
            } else {
                showError(res.msg || '校对失败，请稍后重试');
            }
        } catch (error: any) {
            console.error('纯净版校对接口调用失败:', error);
            showError(error?.message || '校对失败，请稍后重试');
        } finally {
            setIsGenerating(false);
        }
    };

    const handleGenerate = async () => {
        if (!videoData || !videoData.id) {
            showError('视频数据不存在，无法生成');
            return;
        }

        // 防止重复点击
        if (isGenerating) {
            return;
        }

        // 检查是否有目标台词但译文为空
        const emptyTargetSubtitles = targetText.filter(
            (item) => !formatSubtitlePostTextForListDisplay(item.text || '').trim()
        );
        if (emptyTargetSubtitles.length > 0) {
            showError('检测到部分句子缺少译文，请重新翻译配音后合成');
            return;
        }

        try {
            setIsGenerating(true);

            const batchItems =
                videoShow.subtitleOverlayPlayerRef.current?.buildBatchPostTexts();
            if (!batchItems?.length) {
                showError('字幕未就绪，请稍后重试');
                setIsGenerating(false);
                return;
            }

            const batchPayload = buildBatchUpdatePostTextRequestPayload(
                videoData.id,
                batchItems
            );
            const batchRes = await batchUpdatePostTextApi(batchPayload);
            if (batchRes.code !== 0) {
                showError(batchRes.msg || '批量更新目标台词失败');
                setIsGenerating(false);
                return;
            }

            const postTextById = new Map(batchItems.map((b) => [b.id, b.postText]));
            setVideoData((prev) => {
                if (!prev?.subtitles) return prev;
                return {
                    ...prev,
                    subtitles: prev.subtitles.map((s) =>
                        postTextById.has(s.id) ? { ...s, post_text: postTextById.get(s.id)! } : s
                    ),
                };
            });
            setTargetText((prev) =>
                prev.map((item) =>
                    postTextById.has(item.id)
                        ? { ...item, text: postTextById.get(item.id)! }
                        : item
                )
            );

            const generatePayload =
                videoShow.subtitleOverlayPlayerRef.current?.getGenerateVideoRequestPayload(videoData.id);
            if (!generatePayload) {
                showError('字幕样式未就绪，请稍后重试');
                setIsGenerating(false);
                return;
            }
            console.log('开始生成视频，请求参数:', generatePayload);

            const response = await generateVideoApi(generatePayload);
            
            if (response.code === 0) {
                console.log('视频生成成功');
                
                // 将提示信息存储到localStorage，在页面切换后显示
                localStorage.setItem('videoGenerateAlert', JSON.stringify({
                    type: 'success',
                    message: '合成成功',
                    timestamp: Date.now()
                }));
                
                // 请求完成后跳转到对应的视频列表页（与返回按钮逻辑保持一致）
                const finalSeriesId = seriesId || videoData?.series_id;
                if (finalSeriesId) {
                    const seriesNameResolved =
                        currentEpisodeInfo.name?.trim() ||
                        searchParams.get('seriesName')?.trim() ||
                        undefined;
                    navigate(
                        buildPremiumVideoListRefactorReturnAfterVideoEditUrl({
                            seriesId: finalSeriesId,
                            seriesName: seriesNameResolved,
                            langCode: urlLangCode,
                            langName: searchParams.get('langName'),
                            isPure: isFromPure,
                            fromQuick: searchParams.get("from") === "quick",
                            listReturnPage: videoListReturnPage,
                            innerListPage: innerSeriesListReturnPage,
                            outerListPage: outerSeriesListReturnPage,
                        })
                    );
                } else {
                    navigate(PREMIUM_TRANSLATION_FULL_PATH);
                }
            } else {
                console.error('视频生成失败:', response.msg);
                showError(response.msg || '视频生成失败，请重试');
                setIsGenerating(false);
            }
                
        } catch (error: any) {
            console.error('生成视频失败:', error);
            // 提取错误信息，优先使用 API 返回的 msg
            const errorMsg = error?.response?.data?.msg || 
                           error?.response?.msg || 
                           error?.msg || 
                           error?.message || 
                           '视频生成失败，请重试';
            showError(errorMsg);
            setIsGenerating(false);
        }
    };

    const handleGlobalRetranslate = async (customText?: string) => {
        if (!videoData || !videoData.subtitles || isGlobalRetranslating) {
            return;
        }

        setIsGlobalRetranslating(true);
        setShowGlobalRetranslateDropdown(false);
        
        // 步骤1: 过滤出目标台词(postText)为空的台词，创建触发列表
        const subtitlesToTranslate: Array<{
            subtitle: Task.SubtitlesLegacy;
            index: number;
        }> = [];
        
        videoData.subtitles.forEach((subtitle, index) => {
            // 判断目标台词是否为空（post_text为空、null、undefined或只包含空白字符）
            const postText = subtitle.post_text || '';
            const isEmpty = !postText.trim();
            
            if (isEmpty) {
                subtitlesToTranslate.push({
                    subtitle,
                    index
                });
            }
        });
        
        // 如果没有需要翻译的台词，直接返回
        if (subtitlesToTranslate.length === 0) {
            setIsGlobalRetranslating(false);
            showSuccess('所有台词都已翻译，无需重新翻译');
            console.log('✅ 所有台词都已翻译，跳过全局重新翻译');
            return;
        }
        
        console.log(`📋 过滤后的触发列表，共 ${subtitlesToTranslate.length} 条需要翻译的台词:`, 
            subtitlesToTranslate.map(item => ({
                id: item.subtitle.id,
                originText: item.subtitle.origin_text,
                postText: item.subtitle.post_text,
                index: item.index
            }))
        );
        
        // 步骤2: 初始化需要翻译的台词的处理状态
        const initialStates: Record<number, { isRetranslating: boolean; isRedubbing: boolean }> = {};
        subtitlesToTranslate.forEach(({ subtitle }) => {
            initialStates[subtitle.id] = { isRetranslating: true, isRedubbing: false };
        });
        setSubtitleProcessingStates(initialStates);
        
        // 确保状态更新完成后再开始处理
        await new Promise(resolve => setTimeout(resolve, 0));

        try {
            // 步骤3: 根据触发列表执行重新翻译
            const promises = subtitlesToTranslate.map(async ({ subtitle, index }) => {
                try {
                    // 调用翻译API，获取翻译结果（如果 customText 为空，使用默认值）
                    const response = await retranslateSubtitleApi(subtitle.id, customText || undefined);
                    
                    // 更新单个台词的状态为完成
                    console.log(`字幕 ${subtitle.id} (索引 ${index}) 翻译完成，更新状态`);
                    setSubtitleProcessingStates(prev => ({
                        ...prev,
                        [subtitle.id]: { ...prev[subtitle.id], isRetranslating: false }
                    }));
                    
                    // 同步更新右侧台词输入框（targetText）和底部时间轴
                    // 只更新这两个地方，确保按id更新，不混乱
                    if (response && response.post_text !== undefined) {
                        // 1. 更新 targetText 中的 post_text
                        setTargetText(prev => prev.map(item => {
                            if (item.id === subtitle.id) {
                                return {
                                    ...item,
                                    text: response.post_text || ''
                                };
                            }
                            return item;
                        }));
                        
                        // 2. 更新底部时间轴中的翻译后轨道
                        if (timebox.timelineEditorRef.current) {
                            // 构建完整的字幕数据用于更新时间轴
                            const subtitleData: Task.SubtitlesLegacy = {
                                ...subtitle,
                                post_text: response.post_text || '',
                                post_start_time: response.post_start_time || subtitle.post_start_time || subtitle.origin_start_time,
                                post_end_time: response.post_end_time || subtitle.post_end_time || subtitle.origin_end_time,
                                // 重新翻译后，清空 trans_audio，因为需要重新生成翻译音频
                                trans_audio: ''
                            };
                            timebox.timelineEditorRef.current.updateSegment(subtitle.id, subtitleData);
                        }
                        
                        // 3. 更新 videoData.subtitles 中的 post_text / 时间戳 / trans_audio
                        setVideoData(prev => {
                            if (!prev?.subtitles) return prev;
                            return {
                                ...prev,
                                subtitles: prev.subtitles.map(sub => 
                                    sub.id === subtitle.id 
                                        ? {
                                            ...sub,
                                            post_text: response.post_text || '',
                                            post_start_time: response.post_start_time || sub.post_start_time || sub.origin_start_time,
                                            post_end_time: response.post_end_time || sub.post_end_time || sub.origin_end_time,
                                            // 重新翻译后，清空 trans_audio，因为需要重新生成翻译音频
                                            trans_audio: ''
                                        }
                                        : sub
                                )
                            };
                        });
                        
                        console.log(`字幕 ${subtitle.id} (索引 ${index}) 翻译结果已同步到右侧输入框和时间轴`);
                    }
                    
                    return { subtitleId: subtitle.id, index, success: true };
                } catch (error) {
                    console.error(`重新翻译字幕 ${subtitle.id} (索引 ${index}) 失败:`, error);
                    // 更新单个台词的状态为完成（即使失败也要取消禁用状态）
                    console.log(`字幕 ${subtitle.id} (索引 ${index}) 翻译失败，但仍更新状态`);
                    setSubtitleProcessingStates(prev => ({
                        ...prev,
                        [subtitle.id]: { ...prev[subtitle.id], isRetranslating: false }
                    }));
                    return { subtitleId: subtitle.id, index, success: false, error };
                }
            });

            const results = await Promise.all(promises);
            const successCount = results.filter(r => r.success).length;
            const failCount = results.length - successCount;

            if (successCount > 0) {
                showSuccess(`成功重新翻译 ${successCount} 条字幕${failCount > 0 ? `，${failCount} 条失败` : ''}`);
                console.log(`✅ 全局翻译完成，共处理 ${subtitlesToTranslate.length} 条目标台词为空的字幕，${successCount} 条成功，${failCount} 条失败`);
            } else {
                showError('重新翻译失败，请重试');
            }
        } catch (error) {
            console.error('全局重新翻译失败:', error);
            showError('重新翻译失败，请重试');
        } finally {
            setIsGlobalRetranslating(false);
            // 清除所有台词的处理状态
            setSubtitleProcessingStates({});
        }
    };

    const handleGlobalRedub = async () => {
        if (!videoData || !videoData.subtitles || isGlobalRedubbing) {
            return;
        }

        setIsGlobalRedubbing(true);
        
        // 步骤1: 过滤出目标音频(transAudio)为空的台词，创建触发列表
        const subtitlesToRedub: Array<{
            subtitle: Task.SubtitlesLegacy;
            index: number;
        }> = [];
        
        videoData.subtitles.forEach((subtitle, index) => {
            // 判断目标音频是否为空（trans_audio为空、null、undefined或只包含空白字符）
            const transAudio = subtitle.trans_audio || '';
            const isEmpty = !transAudio.trim();
            
            if (isEmpty) {
                subtitlesToRedub.push({
                    subtitle,
                    index
                });
            }
        });
        
        // 如果没有需要配音的台词，直接返回
        if (subtitlesToRedub.length === 0) {
            setIsGlobalRedubbing(false);
            showSuccess('所有台词都已配音，无需重新配音');
            console.log('✅ 所有台词都已配音，跳过全局重新配音');
            return;
        }
        
        console.log(`📋 过滤后的触发列表，共 ${subtitlesToRedub.length} 条需要配音的台词:`, 
            subtitlesToRedub.map(item => ({
                id: item.subtitle.id,
                originText: item.subtitle.origin_text,
                transAudio: item.subtitle.trans_audio,
                index: item.index
            }))
        );

        // 步骤2: 初始化需要配音的台词的处理状态
        const initialStates: Record<number, { isRetranslating: boolean; isRedubbing: boolean }> = {};
        subtitlesToRedub.forEach(({ subtitle }) => {
            initialStates[subtitle.id] = { isRetranslating: false, isRedubbing: true };
        });
        setSubtitleProcessingStates(initialStates);
        
        // 确保状态更新完成后再开始处理
        await new Promise(resolve => setTimeout(resolve, 0));

        try {
            // 步骤3: 根据触发列表执行重新配音
            const promises = subtitlesToRedub.map(async ({ subtitle, index }) => {
                try {
                    // 重新配音接口 subtitleId 始终传当前台词的字幕 id（与单行重新配音一致），不传 publicVoiceToneId
                    const response = await audioRegenerateApi(subtitle.id);
                    
                    // 更新单个台词的状态为完成
                    console.log(`字幕 ${subtitle.id} (索引 ${index}) 配音完成，更新状态`);
                    setSubtitleProcessingStates(prev => ({
                        ...prev,
                        [subtitle.id]: { ...prev[subtitle.id], isRedubbing: false }
                    }));
                    
                    // 同步更新右侧台词输入框（targetText）和底部时间轴
                    // 只更新这两个地方，确保按id更新，不混乱
                    if (response) {
                        // 1. 如果 post_text 有变化，更新 targetText 中的 post_text
                        if (response.post_text !== undefined && response.post_text !== subtitle.post_text) {
                            setTargetText(prev => prev.map(item => {
                                if (item.id === subtitle.id) {
                                    return {
                                        ...item,
                                        text: response.post_text || ''
                                    };
                                }
                                return item;
                            }));
                        }
                        
                        // 2. 如果时间戳有变化，更新 targetText 中的时间范围
                        const newTimeRange = `${response.post_start_time || subtitle.post_start_time || subtitle.origin_start_time} → ${response.post_end_time || subtitle.post_end_time || subtitle.origin_end_time}`;
                        setTargetText(prev => {
                            const currentTargetData = prev.find(item => item.id === subtitle.id);
                            if (currentTargetData && currentTargetData.timeRange !== newTimeRange) {
                                return prev.map(item => {
                                    if (item.id === subtitle.id) {
                                        return {
                                            ...item,
                                            timeRange: newTimeRange
                                        };
                                    }
                                    return item;
                                });
                            }
                            return prev;
                        });
                        
                        // 3. 更新底部时间轴中的翻译后轨道
                        if (timebox.timelineEditorRef.current) {
                            // 构建完整的字幕数据用于更新时间轴
                            const subtitleData: Task.SubtitlesLegacy = {
                                ...subtitle,
                                post_text: response.post_text || subtitle.post_text || '',
                                post_start_time: response.post_start_time || subtitle.post_start_time || subtitle.origin_start_time,
                                post_end_time: response.post_end_time || subtitle.post_end_time || subtitle.origin_end_time,
                                trans_audio: response.trans_audio || subtitle.trans_audio || ''
                            };
                            timebox.timelineEditorRef.current.updateSegment(subtitle.id, subtitleData);
                        }
                        
                        // 4. 更新 videoData.subtitles 中的 trans_audio 和其他可能变化的字段
                        setVideoData(prev => {
                            if (!prev?.subtitles) return prev;
                            
                            return {
                                ...prev,
                                subtitles: prev.subtitles.map(sub => {
                                    if (sub.id === subtitle.id) {
                                        return {
                                            ...sub,
                                            trans_audio: response.trans_audio || sub.trans_audio || '',
                                            post_text: response.post_text !== undefined ? response.post_text : sub.post_text,
                                            post_start_time: response.post_start_time || sub.post_start_time || sub.origin_start_time,
                                            post_end_time: response.post_end_time || sub.post_end_time || sub.origin_end_time
                                        };
                                    }
                                    return sub;
                                })
                            };
                        });
                        
                        console.log(`字幕 ${subtitle.id} (索引 ${index}) 配音结果已同步到右侧输入框和时间轴`);
                    }
                    
                    return { subtitleId: subtitle.id, index, success: true };
                } catch (error) {
                    console.error(`重新配音字幕 ${subtitle.id} (索引 ${index}) 失败:`, error);
                    // 更新单个台词的状态为完成（即使失败也要取消禁用状态）
                    console.log(`字幕 ${subtitle.id} (索引 ${index}) 配音失败，但仍更新状态`);
                    setSubtitleProcessingStates(prev => ({
                        ...prev,
                        [subtitle.id]: { ...prev[subtitle.id], isRedubbing: false }
                    }));
                    return { subtitleId: subtitle.id, index, success: false, error };
                }
            });

            const results = await Promise.all(promises);
            const successCount = results.filter(r => r.success).length;
            const failCount = results.length - successCount;

            if (successCount > 0) {
                showSuccess(`成功重新配音 ${successCount} 条字幕${failCount > 0 ? `，${failCount} 条失败` : ''}`);
                console.log(`✅ 全局配音完成，共处理 ${subtitlesToRedub.length} 条目标音频为空的字幕，${successCount} 条成功，${failCount} 条失败`);
            } else {
                showError('重新配音失败，请重试');
            }
        } catch (error) {
            console.error('全局重新配音失败:', error);
            showError('重新配音失败，请重试');
        } finally {
            setIsGlobalRedubbing(false);
            // 清除所有台词的处理状态
            setSubtitleProcessingStates({});
        }
    };

    return {
        // Alert
        alertConfig,
        hideAlert,
        showError,
        showSuccess,
        // Page state
        videoData,
        setVideoData,
        setVideoDataSafe,
        videoName,
        isFromPure,
        isGenerating,
        isGlobalRetranslating,
        isGlobalRedubbing,
        subtitleProcessingStates,
        showGlobalRetranslateDropdown,
        setShowGlobalRetranslateDropdown,
        globalCustomText,
        setGlobalCustomText,
        isArrowHovered,
        showBackTranslate,
        setShowBackTranslate,
        backTranslateData,
        setBackTranslateData,
        isBackTranslating,
        setIsBackTranslating,
        clearGlobalRetranslateCloseTimer,
        openGlobalRetranslateDropdown,
        scheduleCloseGlobalRetranslateDropdown,
        currentEpisodeInfo,
        userInfo,
        displayName,
        id,
        isAdmin,
        isUser: Boolean(roleInfo?.isUser),
        speakerLineCounts,
        // Page-level memos
        videoDataForDemo,
        localSubtitlesForDemo,
        // Page-level handlers
        handleBack,
        handleProofread,
        handleGenerate,
        handleSubtitlePostTextChange,
        handleTargetDialogueClick,
        handleGlobalRetranslate,
        handleGlobalRedub,
        // From timebox
        zoomLevel: timebox.zoomLevel,
        setZoomLevel: timebox.setZoomLevel,
        isAdjustingSpeed: timebox.isAdjustingSpeed,
        pendingTimelineUpdates: timebox.pendingTimelineUpdates,
        timelineSelectedSubtitleId: timebox.timelineSelectedSubtitleId,
        timelineActiveTrack: timebox.timelineActiveTrack,
        timelineEditorRef: timebox.timelineEditorRef,
        timelineScrubbingRef: timebox.timelineScrubbingRef,
        playheadTotalTimeRef: timebox.playheadTotalTimeRef,
        handleZoomChange: timebox.handleZoomChange,
        handleZoomAfterChange: timebox.handleZoomAfterChange,
        handleZoomIn: timebox.handleZoomIn,
        handleZoomOut: timebox.handleZoomOut,
        getTimelineTotalTimeSec: timebox.getTimelineTotalTimeSec,
        translatedTimelineModels: timebox.translatedTimelineModels,
        // From textList
        sourcesText: textList.sourcesText,
        targetText,
        setTargetText,
        targetTextForDemo: textList.targetTextForDemo,
        textListRef: textList.textListRef,
        roles: textList.roles,
        seriesSpeakers: textList.seriesSpeakers,
        setSeriesSpeakers: textList.setSeriesSpeakers,
        rowSelectedRoles: textList.rowSelectedRoles,
        editingRoleKey: textList.editingRoleKey,
        editingRoleName: textList.editingRoleName,
        setEditingRoleName: textList.setEditingRoleName,
        highlightedSubtitleId: textList.highlightedSubtitleId,
        updatingSubtitles: textList.updatingSubtitles,
        subtitleUpdateErrors: textList.subtitleUpdateErrors,
        seriesVoiceLibraryMarkers: textList.seriesVoiceLibraryMarkers,
        openHistorySubtitleId: textList.openHistorySubtitleId,
        volumeMap: textList.volumeMap,
        speedMap: textList.speedMap,
        handleUpdateVolume: textList.handleUpdateVolume,
        handleUpdateSpeed: textList.handleUpdateSpeed,
        handleSourceDataChange: textList.handleSourceDataChange,
        handleTargetDataChange: textList.handleTargetDataChange,
        handleRoleSelect: textList.handleRoleSelect,
        handleNewSpeaker: textList.handleNewSpeaker,
        handleDeleteRole: textList.handleDeleteRole,
        handleStartEditRole: textList.handleStartEditRole,
        handleSaveRoleEdit: textList.handleSaveRoleEdit,
        handleCancelRoleEdit: textList.handleCancelRoleEdit,
        handlePromoteToSeriesSpeaker: textList.handlePromoteToSeriesSpeaker,
        handleDeleteSubtitle: textList.handleDeleteSubtitle,
        handleAddSubtitle: textList.handleAddSubtitle,
        handleSubtitleUpdate: textList.handleSubtitleUpdate,
        handleGetNextSubtitle: textList.handleGetNextSubtitle,
        handleGetNextSubtitleId: textList.handleGetNextSubtitleId,
        handleGetFullTimelineData: textList.handleGetFullTimelineData,
        handleRefreshSubtitles: textList.handleRefreshSubtitles,
        handleOpenHistoryPanel: textList.handleOpenHistoryPanel,
        handleCloseHistoryPanel: textList.handleCloseHistoryPanel,
        handleUpdateVideoDataSpeed: textList.handleUpdateVideoDataSpeed,
        ensureBackgroundGainNode: videoShow.ensureBackgroundGainNode,
        resumeBackgroundAudioContextIfSuspended: videoShow.resumeBackgroundAudioContextIfSuspended,
        backgroundGainNodeRef: videoShow.backgroundGainNodeRef,
        vocalsHlsRef: videoShow.vocalsHlsRef,
        // From videoShow
        isLoadingVideo: videoShow.isLoadingVideo,
        setIsLoadingVideo: videoShow.setIsLoadingVideo,
        showBackgroundVolumePopup: videoShow.showBackgroundVolumePopup,
        setShowBackgroundVolumePopup: videoShow.setShowBackgroundVolumePopup,
        backgroundVolume: videoShow.backgroundVolume,
        setBackgroundVolume: videoShow.setBackgroundVolume,
        backgroundVolumePopupPosition: videoShow.backgroundVolumePopupPosition,
        setBackgroundVolumePopupPosition: videoShow.setBackgroundVolumePopupPosition,
        isPlaying: videoShow.isPlaying,
        setIsPlaying: videoShow.setIsPlaying,
        duration: videoShow.duration,
        sourceVideoRef: videoShow.sourceVideoRef,
        subtitleOverlayPlayerRef: videoShow.subtitleOverlayPlayerRef,
        backgroundAudioRef: videoShow.backgroundAudioRef,
        vocalsAudioRef: videoShow.vocalsAudioRef,
        backgroundAudioEnabled: videoShow.backgroundAudioEnabled,
        toggleBackgroundAudio: videoShow.toggleBackgroundAudio,
        togglePlay: videoShow.togglePlay,
        seekToTime: videoShow.seekToTime,
        handleSwitchTrack: videoShow.handleSwitchTrack,
        handlePlayheadMove: videoShow.handlePlayheadMove,
        pauseAudioForEditing: videoShow.pauseAudioForEditing,
        syncAudioAfterEditing: videoShow.syncAudioAfterEditing,
        playSubtitle: videoShow.playSubtitle,
        playTargetSubtitle: videoShow.playTargetSubtitle,
        handleTimeUpdate: videoShow.handleTimeUpdate,
        handleVideoEnded: videoShow.handleVideoEnded,
        handleLoadedMetadata: videoShow.handleLoadedMetadata,
        handleRightLoadedMetadata: videoShow.handleRightLoadedMetadata,
        handleExternalPlay: videoShow.handleExternalPlay,
        handleExternalPause: videoShow.handleExternalPause,
        handleExternalSeek: videoShow.handleExternalSeek,
        handleRightVideoReady: videoShow.handleRightVideoReady,
        leftVideoReady: videoShow.leftVideoReady,
        rightVideoReady: videoShow.rightVideoReady,
        toggleRightVideoPlay: videoShow.toggleRightVideoPlay,
        handleRightVideoSeek: videoShow.handleRightVideoSeek,
        ensureAudioPlayAndSync: videoShow.ensureAudioPlayAndSync,
        userInteractionRef: videoShow.userInteractionRef,
        formatTime: videoShow.formatTime,
        leftVideoAspect: videoShow.leftVideoAspect,
    };
};
